import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Map, Home, RefreshCw, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';

export function NotFound() {
    const navigate = useNavigate();
    const posthog = usePostHog();

    useEffect(() => {
        posthog?.capture('404_viewed', { path: window.location.pathname });
    }, [posthog]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-6 overflow-hidden relative">
            {/* Abstract Background Elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-500/10 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-violet-500/10 rounded-full blur-[120px]" />
            </div>

            <div className="max-w-md w-full text-center relative z-10">
                <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className="mb-8 relative inline-block"
                >
                    <div className="w-32 h-32 bg-indigo-100 dark:bg-indigo-900/30 rounded-3xl flex items-center justify-center mx-auto transform rotate-12">
                        <Map className="w-16 h-16 text-indigo-600 dark:text-indigo-400 -rotate-12" />
                    </div>
                    <motion.div
                        animate={{ y: [0, -10, 0] }}
                        transition={{ repeat: Infinity, duration: 2 }}
                        className="absolute -top-4 -right-4 w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center border-4 border-slate-50 dark:border-slate-950"
                    >
                        <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                    </motion.div>
                </motion.div>

                <h1 className="text-6xl font-black text-slate-900 dark:text-white mb-4 tracking-tighter italic">404</h1>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-4">You've Wandered Off Trail!</h2>

                <p className="text-slate-600 dark:text-slate-400 mb-10 leading-relaxed">
                    This path doesn't exist on our map. Even the best athletes miss a turn sometimes. Let's get you back to the base camp before you hit the wall.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                        onClick={() => navigate('/')}
                        size="lg"
                        className="rounded-2xl h-14 px-8 bg-indigo-600 hover:bg-indigo-700 font-bold group"
                    >
                        <Home className="w-5 h-5 mr-2 group-hover:-translate-y-0.5 transition-transform" />
                        Back to Base
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => window.location.reload()}
                        size="lg"
                        className="rounded-2xl h-14 px-8 border-slate-300 dark:border-slate-700 font-bold"
                    >
                        <RefreshCw className="w-5 h-5 mr-2" />
                        Check Again
                    </Button>
                </div>

                <div className="mt-16 text-slate-400 dark:text-slate-600 text-xs font-medium uppercase tracking-widest">
                    Adaptive Endurance • Training Engine
                </div>
            </div>
        </div>
    );
}

export function ServerError({ error }: { error?: Error }) {
    const posthog = usePostHog();

    useEffect(() => {
        posthog?.capture('500_server_error', {
            message: error?.message,
            stack: error?.stack
        });
    }, [posthog, error]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-900 p-6 overflow-hidden relative text-white">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-indigo-500/20 via-slate-900 to-slate-900" />

            <div className="max-w-md w-full text-center relative z-10">
                <motion.div
                    animate={{ rotate: [0, -5, 5, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                    className="w-32 h-32 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-8 border border-red-500/30"
                >
                    <div className="text-5xl">🔋</div>
                </motion.div>

                <h1 className="text-4xl font-black mb-4 tracking-tight uppercase">Engine Overheat</h1>
                <h2 className="text-xl font-bold text-red-400 mb-6 italic">The Tool Hits "The Wall" (Bonked)</h2>

                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-10 text-left">
                    <p className="text-slate-300 mb-4 text-sm leading-relaxed">
                        We forgot to take our gels at mile 20. The server is currently experiencing a massive energy crash. Our team of mechanics is already out there with dynamic stretches and glucose.
                    </p>
                    {error && (
                        <code className="block text-[10px] text-red-300/60 break-all font-mono bg-black/40 p-3 rounded-lg border border-red-900/30">
                            {error.message}
                        </code>
                    )}
                </div>

                <Button
                    onClick={() => window.location.assign('/')}
                    size="lg"
                    className="w-full rounded-2xl h-14 bg-white text-slate-900 hover:bg-slate-100 font-black shadow-xl shadow-white/5"
                >
                    REFUEL & RESTART
                </Button>

                <p className="mt-8 text-slate-500 text-[10px] font-bold tracking-widest uppercase">
                    Error Logged for Analysis
                </p>
            </div>
        </div>
    );
}
